import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import React, { useRef, useState, useEffect } from "react";

import ReactPlayer from "react-player";

/**
 * PRODUCTION VIDEO SETUP:
 *
 * For optimal production performance, replace VIDEO_URL with:
 *
 * 1. VIMEO PRO (Recommended):
 *    const VIDEO_URL = "https://vimeo.com/your-video-id";
 *
 * 2. CLOUDINARY:
 *    const VIDEO_URL = "https://res.cloudinary.com/your-cloud/video/upload/your-video.mp4";
 *
 * 3. AWS CLOUDFRONT:
 *    const VIDEO_URL = "https://your-cloudfront-domain.com/showreel.mp4";
 *
 * 4. YOUTUBE (Public videos):
 *    const VIDEO_URL = "https://www.youtube.com/watch?v=your-video-id";
 *
 * Current setup supports all these platforms with automatic optimization.
 */

const HomeHeroSection = ({ videoMuted, videoReady, isPlaying, playerRef, setVideoMuted, setIsPlaying, setVideoReady }) => {
  const sec1 = useRef();
  const homeRef = useRef();
  const heroTextRef = useRef();
  const logoSvgRef = useRef();
  const mainSvgLogoRef = useRef();
  const [isFullscreenVideo, setIsFullscreenVideo] = useState(false);

  // Production video URL - Replace with your actual video URL
  // const VIDEO_URL = "https://ik.imagekit.io/x5xessyka/digicots/showreel.webm/ik-video.mp4?tr=orig"; // Change this to your cloud video URL
  const VIDEO_URL = "/videos/showreel.webm"; // Change this to your cloud video URL
  
  // Poster image for mobile/tablet
  const POSTER_IMAGE = "/new-final-data/contact/Contact.webp"; // Change this to your poster image path

  // Handle fullscreen video for mobile/tablet
  const handlePlayButtonClick = () => {
    setIsFullscreenVideo(true);
    setIsPlaying(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreenVideo(false);
    setIsPlaying(false);
  };

  // Video health monitoring and performance optimization
  useEffect(() => {
    let healthCheckInterval;

    const checkVideoHealth = () => {
      if (playerRef.current) {
        try {
          const player = playerRef.current;

          // Check if getInternalPlayer method exists
          if (typeof player.getInternalPlayer === "function") {
            const internalPlayer = player.getInternalPlayer();

            if (internalPlayer && internalPlayer.readyState >= 2) {
              // Video is ready
              if (!videoReady) {
                setVideoReady(true);
              }

              // Force autoplay for Safari mobile
              if (internalPlayer.paused && !internalPlayer.ended && isPlaying) {
                console.log("Video health check: resuming paused video");
                internalPlayer.play().catch((error) => {
                  console.log("Autoplay failed, will retry on user interaction:", error);
                });
              }
            }
          } else {
            // Fallback: just set video as ready after a delay
            if (!videoReady) {
              setTimeout(() => setVideoReady(true), 2000);
            }
          }
        } catch (error) {
          console.log("Video health check error:", error);
          // Fallback: set video as ready
          if (!videoReady) {
            setVideoReady(true);
          }
        }
      }
    };

    // Start health monitoring
    healthCheckInterval = setInterval(checkVideoHealth, 2000);

    // Safari autoplay fix - retry on user interaction
    const handleUserInteraction = () => {
      if (playerRef.current) {
        try {
          const player = playerRef.current;
          if (typeof player.getInternalPlayer === "function") {
            const internalPlayer = player.getInternalPlayer();
            if (internalPlayer && internalPlayer.paused && isPlaying) {
              internalPlayer.play().catch(console.error);
            }
          }
        } catch (error) {
          console.log("User interaction video play error:", error);
        }
      }
    };

    // Add event listeners for user interaction
    const events = ['touchstart', 'click', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });

    return () => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
      // Clean up event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [videoReady, isPlaying]);

  // Handle video mute toggle with performance optimization


  gsap.registerPlugin(ScrollTrigger);
  useGSAP(() => {
    // Only run GSAP animations on XL screens (desktop)
    if (window.innerWidth >= 1280) {
      // Main timeline with optimized scroll trigger
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: homeRef.current,
          start: "top 0",
          end: "top -100%",
          scrub: 1.2,
          // anticipatePin: 1,
          // refreshPriority: -1,
          // invalidateOnRefresh: true,
          // markers: true, // Remove in production
        },
      });

      // Set initial states
      gsap.set([sec1.current], { y: "100%" });
      gsap.set(heroTextRef.current, { opacity: 1 });

      // Animation sequence
      tl.to(
        sec1.current,
        {
          y: "0%",
          duration: 3.1,
          ease: "power3.out",
        }
        // 0.2
      );
    }
  }, [
    sec1.current,
    homeRef.current,
    heroTextRef.current,
    logoSvgRef.current,
    mainSvgLogoRef.current,
  ]);

  return (
    <>
      {/* Fullscreen Video Modal for Mobile/Tablet */}
      {isFullscreenVideo && (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center">
          <button
            onClick={handleCloseFullscreen}
            className="absolute scale-[1.5] top-20 right-2 z-[10000] text-white bg-black/50 rounded-full p-3 hover:bg-black/70 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <ReactPlayer
            className="w-full h-full"
            url={VIDEO_URL}
            src={VIDEO_URL}
            playing={true}
            loop={true}
            muted={false}
            controls={true}
            width="100%"
            height="100%"
            config={{
              file: {
                attributes: {
                  playsinline: false, // Allow fullscreen on mobile
                },
              },
            }}
          />
        </div>
      )}

      <div id="HomeHero" className="section xl:h-[200vh] h-auto w-screen relative">
        <div
          ref={homeRef}
          className="xl:h-screen h-auto w-screen xl:sticky xl:top-0 overflow-hidden"
        >
          {/* Desktop Video Background (XL screens only) */}
          <div className="hidden xl:block top-0 h-full w-full relative px-10">
            {/* Production-Level Video Background Container */}
            <div
              className="absolute inset-0 h-screen w-full dark:bg-black bg-white"
              style={{
                contain: "layout style paint",
                willChange: "transform",
                transform: "translate3d(0, 0, 0)",
              }}
            >
              <ReactPlayer
                className="h-full w-full object-cover absolute"
                ref={playerRef}
                url={VIDEO_URL}
                src={VIDEO_URL}
                playing={isPlaying}
                loop={true}
                muted={videoMuted}
                width="100%"
                height="100%"
                playsinline={true}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
                config={{
                  file: {
                    attributes: {
                      preload: "metadata",
                      playsinline: true,
                      playsInline: true,
                      autoPlay: true,
                      muted: true,
                      disablePictureInPicture: true,
                      disableRemotePlayback: true,
                      style: {
                        objectFit: "cover",
                        filter: "brightness(0.9)",
                        willChange: "auto",
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "translate3d(0, 0, 0)",
                        WebkitTransform: "translate3d(0, 0, 0)",
                        imageRendering: "optimizeSpeed",
                        WebkitImageRendering: "optimizeSpeed",
                      },
                    },
                    forceVideo: true,
                    forceHLS: false,
                    forceDASH: false,
                  },
                  vimeo: {
                    playerOptions: {
                      background: true,
                      autoplay: true,
                      loop: true,
                      muted: true,
                      quality: "auto",
                      responsive: true,
                      playsinline: true,
                    },
                  },
                  youtube: {
                    playerVars: {
                      autoplay: 1,
                      controls: 0,
                      rel: 0,
                      showinfo: 0,
                      mute: 1,
                      loop: 1,
                      playsinline: 1,
                      playlist: "",
                    },
                  },
                }}
              />
            </div>
            {/* Bottom decoration */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>

          {/* Mobile/Tablet Image with Play Button */}
          <div className="xl:hidden relative w-full h-screen bg-cover bg-[65%] bg-no-repeat" 
               style={{ backgroundImage: `url('${POSTER_IMAGE}')` }}>
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-black/30"></div>
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={handlePlayButtonClick}
                className="group relative bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-full p-6 hover:bg-white/30 hover:border-white/50 transition-all duration-300 transform hover:scale-110"
              >
                <svg 
                  className="w-16 h-16 text-white ml-1" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z"/>
                </svg>
                
                {/* Pulse animation */}
                <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
                <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-pulse"></div>
              </button>
            </div>

            {/* Mobile Text Content */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-center text-white">
              <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg">
                DREAM TO<br />DELIVER
              </h1>
              <p className="text-lg md:text-xl font-medium opacity-90 max-w-2xl mx-auto drop-shadow">
               Digicots is a  digital marketing agency that thrives on turning creative <br/> ideas into impactful and measurable outcomes.
              </p>
            </div>
          </div>

          {/* Desktop Text Mask Overlay (XL screens only) */}
          <div
            ref={sec1}
            className="section hidden xl:block h-screen w-full absolute top-0 left-0 right-0 z-20 bg-white/[.1] backdrop-blur dark:bg-black/[.1]"
          >
            {/* SVG Text Mask Implementation */}
            <div className="relative w-full h-full">
              {/* Desktop SVG */}
              <svg
                className="absolute top-0 left-0 pointer-events-none h-screen w-screen"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 1920 1080"
                preserveAspectRatio="xMidYMid slice"
              >
                <defs>
                  <mask id="text-mask-desktop" maskUnits="userSpaceOnUse">
                    <rect width="100%" height="100%" fill="white" />
                    <text
                      x="960"
                      y="420"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="black"
                      fontSize="260"
                      fontFamily="Arial, sans-serif"
                      fontWeight="900"
                      letterSpacing="0.02em"
                    >
                      DREAM TO
                    </text>
                    <text
                      x="960"
                      y="645"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="black"
                      fontSize="260"
                      fontFamily="Arial, sans-serif"
                      fontWeight="900"
                      letterSpacing="0.02em"
                    >
                      DELIVER
                    </text>
                  </mask>
                </defs>
                {/* Overlay with text cut out - changes color based on theme */}
                <rect
                  width="100%"
                  height="100%"
                  fill="currentColor"
                  mask="url(#text-mask-desktop)"
                  className="text-white dark:text-black"
                />
              </svg>

              <div className="text-zinc-800 dark:text-zinc-200 text-center font-semibold text-2xl 2xl:text-3xl absolute bottom-25 w-full">
                 Digicots is a  digital marketing agency that thrives on turning creative <br/> ideas into impactful and measurable outcomes.
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default HomeHeroSection;
